import React, { useRef } from 'react';
import { formatCurrency } from '@/lib/payrollCalc';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const AbdLogo = ({ height = 28 }) => (
  <svg
    aria-hidden="true"
    fill="none"
    viewBox="18 8 48 42"
    xmlns="http://www.w3.org/2000/svg"
    style={{ height, width: 'auto' }}
  >
    <path
      clipRule="evenodd"
      d="M22.533 38.094h24.408c.313 0 .04-.234-.117-.586l-1.835-3.4a15.982 15.982 0 0 1-.742-1.406H32.375v-.117l3.436-6.251c.703-1.212 1.68-3.165 2.343-4.298l2.343-4.298c.196-.351 1.016-1.992 1.211-2.149.04.196.117.313.235.47l.742 1.328c.859 1.445 1.718 3.047 2.577 4.454 1.015 1.719 2.07 3.672 3.085 5.352l2.07 3.595c.156.273.312.586.508.898.351.586.664 1.172 1.054 1.798.352.586.664 1.172 1.015 1.797l4.14 7.189c.117.234.976 1.602 1.015 1.797H18.667v5.353h46.121c.118 0 .157-.04.157-.156v-4.845c0-.196-.47-.938-.586-1.133l-3.163-5.51c-.118-.195-.196-.35-.313-.546L54.83 26.92c-.117-.196-.195-.352-.313-.547l-6.053-10.471c-.117-.196-.195-.352-.312-.547L45.3 10.393c-.156-.235-.586-.938-.625-1.172h-5.819c-.039.195-.351.664-.468.898-.196.313-.313.586-.508.899l-1.015 1.797c-.196.313-.313.586-.508.899a6.875 6.875 0 0 1-.547.899l-2.07 3.633c-.351.625-.663 1.211-1.015 1.797l-6.248 10.862c-.352.625-.664 1.211-1.016 1.797l-2.616 4.532c-.274.508-.586.82-.274.82"
      fill="#B5171B"
      fillRule="evenodd"
    />
  </svg>
);

export function AdvanceSlipModal({ open, onOpenChange, employee, advance }) {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const empName =
      employee?.firstName || employee?.lastName
        ? `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim()
        : advance?.employeeName || 'Employee';
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Advance Slip - ${empName} - ${advance?.advanceId || ''}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Inter:wght@400;500&family=JetBrains+Mono:wght@400;500&display=swap');
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Inter', sans-serif; padding: 40px; color: #0f172a; background: #fff; }
            h1, h2, h3 { font-family: 'Manrope', sans-serif; }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 300);
  };

  if (!advance) return null;

  const empName =
    employee?.firstName || employee?.lastName
      ? `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim()
      : advance?.employeeName || '-';
  const total = Number(advance?.totalAmount) || 0;
  const remaining = Number(advance?.remainingAmount) || 0;
  const recovered = Math.max(total - remaining, 0);
  const monthly = Number(advance?.monthlyDeduction) || 0;
  const startLabel =
    advance?.startMonth && advance?.startYear
      ? `${MONTHS[advance.startMonth - 1] || advance.startMonth} ${advance.startYear}`
      : '-';
  const history = Array.isArray(advance?.deductionHistory) ? advance.deductionHistory : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col gap-3 p-4 sm:p-6">
        <DialogTitle className="sr-only">
          Advance Slip - {empName} - {advance.advanceId}
        </DialogTitle>

        <div ref={printRef} className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0047AB', paddingBottom: '12px', marginBottom: '14px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <AbdLogo height={36} />
                <h1 style={{ fontFamily: 'Manrope, sans-serif', fontSize: '24px', fontWeight: 700, color: '#B5171B', margin: 0 }}>
                  ABD
                </h1>
              </div>
              <p style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Salary Advance Slip</p>
            </div>
            <div style={{ textAlign: 'right', paddingRight: '32px' }}>
              <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>Advance ID</p>
              <p style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace', margin: 0 }}>{advance.advanceId}</p>
              <p style={{ fontSize: '12px', color: '#64748b', margin: '6px 0 0' }}>Status</p>
              <p style={{ fontSize: '14px', fontWeight: 600, margin: 0 }}>{advance.status || '-'}</p>
            </div>
          </div>

          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>Employee Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 32px', marginBottom: '14px', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>Name</span>
              <span style={{ fontWeight: 500 }}>{empName}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>Employee ID</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{advance.employeeId || employee?.empId || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>Department</span>
              <span style={{ fontWeight: 500 }}>{employee?.department || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>Designation</span>
              <span style={{ fontWeight: 500 }}>{employee?.designation || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>Bank Account</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee?.bankAccount || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>PAN</span>
              <span style={{ fontWeight: 500, fontFamily: 'JetBrains Mono, monospace' }}>{employee?.panNumber || '-'}</span>
            </div>
          </div>

          <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, marginBottom: '6px' }}>Advance Details</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '8px 12px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600 }}>
            <span>Particulars</span>
            <span style={{ textAlign: 'right' }}>Amount / Value</span>
          </div>
          {[
            ['Total Advance Amount', formatCurrency(total)],
            ['Monthly Deduction', formatCurrency(monthly)],
            ['Start Month', startLabel],
            ['Amount Recovered', formatCurrency(recovered)],
            ['Amount Remaining', formatCurrency(remaining)],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '6px 12px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
              <span>{label}</span>
              <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 500 }}>{value}</span>
            </div>
          ))}

          {history.length > 0 && (
            <>
              <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '2px', color: '#64748b', fontWeight: 600, margin: '12px 0 6px' }}>Deduction History</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', padding: '8px 12px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', color: '#64748b', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600 }}>
                <span>Period</span>
                <span>Pay Run</span>
                <span style={{ textAlign: 'right' }}>Amount</span>
              </div>
              {history.map((d, i) => (
                <div key={`${d?.payRunId || i}-${d?.month}-${d?.year}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', padding: '6px 12px', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}>
                  <span>{d?.month ? `${MONTHS[d.month - 1] || d.month} ${d.year || ''}` : '-'}</span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}>{d?.payRunId || '-'}</span>
                  <span style={{ textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: '#ef4444' }}>-{formatCurrency(d?.amount || 0)}</span>
                </div>
              ))}
            </>
          )}

          <div style={{ background: '#0047AB', color: 'white', padding: '12px 18px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>ADVANCE AMOUNT</span>
            <span style={{ fontSize: '22px', fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(total)}</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px', marginBottom: '4px' }}>
            <div
              style={{
                border: '4px double #15803d',
                borderRadius: '8px',
                padding: '10px 18px',
                background: 'rgba(240, 253, 244, 0.35)',
                boxShadow: 'inset 0 0 0 2px rgba(21, 128, 61, 0.35)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                minWidth: '240px',
              }}
            >
              <AbdLogo height={36} />
              <div style={{ textAlign: 'center' }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '28px',
                    fontWeight: 800,
                    letterSpacing: '6px',
                    color: '#15803d',
                    lineHeight: 1.15,
                  }}
                >
                  APPROVED
                </p>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#166534',
                  }}
                >
                  Official Advance Authorization
                </p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>
            This is a computer-generated advance slip and does not require a signature.
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-2 pt-1 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={handlePrint} className="bg-[#0047AB] hover:bg-[#003d96] text-white" data-testid="print-advance-slip-btn">
            <Printer className="w-4 h-4 mr-2" /> Print Advance Slip
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
